import { Raycaster, Vector3 } from "three";
import { Context } from "../../context";
import { ControllerStrategy, CameraModeOptions } from "../strategy/strategy";
import { CameraMode } from "../controller";

export class JumbotronControllerStrategy extends ControllerStrategy {
  private initialised = false;
  private contextRef?: Context;

  private raycaster: Raycaster = new Raycaster();

  private prevTarget = new Vector3();

  // TODO: centralise this into own strategy (from PAIR)
  private zoomLevel: {
    min: number;
    max: number;
    current: number;
  } = {
    current: 1,
    min: 1,
    max: 4,
  };
  private targetAngle = 20; // deg
  private timeout?: any;
  private zoomTimeoutMs = 3000;
  private wheelEventOptions: AddEventListenerOptions = { passive: true };
  private mouseZoomCb = (e: WheelEvent) => {
    if (this.timeout !== undefined) {
      clearTimeout(this.timeout);
    }
    this.timeout = setTimeout(() => {
      this.registered?.controls?.zoomTo(this.zoomLevel.current, true);
    }, this.zoomTimeoutMs);
  };
  private camPos = new Vector3();
  private camDir = new Vector3();

  init(context: Context, _options: CameraModeOptions): ControllerStrategy {
    super.init(context);

    this.contextRef = context;

    if (!this.registered || !this.registered.controls) {
      console.error("no controller registered for strikezone camera strategy");
      return this;
    }

    this.registered.controls.getTarget(this.prevTarget);

    this.registered.domRef.addEventListener(
      "wheel",
      this.mouseZoomCb,
      this.wheelEventOptions
    );

    return this;
  }

  update(deltaS: number, gameTimeDelta: number, frameNum?: number) {
    super.update(deltaS, gameTimeDelta, frameNum);
    if (!this.registered) {
      console.error("no controls registered");
      return;
    }
    const { controls, state } = this.registered;

    const jumbotron = this.contextRef?.world?.stadium.jumbotron;

    // lazy register for play state updates here because if we do it in init, the callback
    // triggering steps on itself (setMode gets called simultaneously in different threads)
    if (!this.initialised && jumbotron?.screen) {
      state.tracked = jumbotron.screen;

      controls?.getPosition(this.camPos);
      const angle = jumbotron.arcAngle(this.camPos);

      const factor = this.targetAngle / angle;
      this.zoomLevel.current = Math.min(
        Math.max(factor, this.zoomLevel.min),
        this.zoomLevel.max
      );

      this.registered.setMode(CameraMode.LOOK_AT_SIMPLE, { fromManager: true });
      controls?.zoomTo(this.zoomLevel.current, true);

      // hide any players in way
      // TODO: do we want to run this every update? would need to debounce it
      this.camDir
        .subVectors(jumbotron.screen.position, this.camPos)
        .normalize();
      this.raycaster.set(this.camPos, this.camDir);
      const actors = this.registered.contextRef.world?.actors.getActors();
      if (actors) {
        for (let actor of actors) {
          actor.hide = this.raycaster.ray.intersectsBox(actor.hitbox);
        }
      }

      this.initialised = true;
    }
  }

  teardown(): void {
    super.teardown();

    if (!this.registered || !this.registered.controls) return;

    const jumbotron = this.contextRef?.world?.stadium.jumbotron;

    this.registered.state.tracked = undefined;

    this.registered.domRef.removeEventListener(
      "wheel",
      this.mouseZoomCb,
      this.wheelEventOptions
    );

    const actors = this.registered.contextRef.world?.actors.getActors();
    if (actors) {
      for (let actor of actors) {
        actor.hide = false;
      }
    }
  }
}
